import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../api";

const DEFAULT_COLUMNS = [
  "item_no",
  "wl_no",
  "hsn_sac",
  "item_name",
  "description",
  "unit",
  "qty",
  "rate",
  "total",
  "wrongly_formatted",
];

const EDITABLE_COLUMNS = new Set([
  "item_no",
  "wl_no",
  "hsn_sac",
  "item_name",
  "description",
  "unit",
  "qty",
  "rate",
  "total",
  "payment_terms",
]);

const NUMERIC_COLUMNS = new Set(["qty", "rate", "total", "confidence"]);

const labelMap = {
  item_no: "Item No",
  wl_no: "WLI No",
  hsn_sac: "HSN / SAC",
  item_name: "Item Name",
  description: "Description",
  unit: "Unit",
  qty: "Qty",
  rate: "Rate",
  total: "Total",
  payment_terms: "Payment Terms",
  confidence: "Confidence",
  raw_text: "Raw Text",
  needs_review: "Needs Review",
  wrongly_formatted: "Wrongly Formatted",
  validation_issues: "Validation Issues",
  page_number: "Page",
  row_index: "Row",
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizePreviewItem = (item, sacCode) => ({
  wlino: item.wl_no || item.wlino || item.item_no || "",
  hsnSac: item.hsn_sac || item.hsnSac || sacCode || "",
  itemName: item.item_name || item.itemName || "",
  description: item.description || item.item_name || item.itemName || "",
  unit: item.unit || "",
  quantity: toNumber(item.qty ?? item.quantity),
  price: toNumber(item.rate ?? item.price),
  total: toNumber(item.total),
  measurementType: "quantity",
  paymentMode: "STANDARD",
  paymentTerms: [],
});

const normalizeEditableItem = (item) => ({
  ...item,
  item_no: item.item_no ?? "",
  wl_no: item.wl_no ?? "",
  hsn_sac: item.hsn_sac ?? "",
  item_name: item.item_name ?? "",
  description: item.description ?? "",
  unit: item.unit ?? "",
  qty: item.qty ?? "",
  rate: item.rate ?? "",
  total: item.total ?? "",
  payment_terms: item.payment_terms ?? "",
});

const BoqImportModal = ({ show, onClose, onApply, sacCode }) => {
  const [file, setFile] = useState(null);
  const [engine, setEngine] = useState("python");
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [editableItems, setEditableItems] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLUMNS);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setEngine("python");
      setLoading(false);
      setParsed(null);
      setEditableItems([]);
      setSelectedColumns(DEFAULT_COLUMNS);
    }
  }, [show]);

  const availableColumns = useMemo(
    () => parsed?.availableColumns || [],
    [parsed],
  );
  const items = editableItems;

  const visibleColumns = useMemo(
    () => selectedColumns.filter((column) => availableColumns.includes(column)),
    [availableColumns, selectedColumns],
  );

  const updateItem = (index, column, value) => {
    setEditableItems((current) =>
      current.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const nextValue = NUMERIC_COLUMNS.has(column)
          ? (value === "" ? "" : value)
          : value;
        return {
          ...item,
          [column]: nextValue,
        };
      }),
    );
  };

  const parseFile = async () => {
    if (!file) {
      swal("Warning", "Please choose a PDF file first", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("engine", engine);

    try {
      setLoading(true);
      const res = await api.post("/work-orders/preview-boq", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        setParsed(res.data.data);
        setEditableItems(
          Array.isArray(res.data.data?.items)
            ? res.data.data.items.map((item) => normalizeEditableItem(item))
            : [],
        );
        setSelectedColumns(
          res.data.data?.suggestedColumns?.length
            ? res.data.data.suggestedColumns
            : DEFAULT_COLUMNS,
        );
      } else {
        throw new Error(res.data?.message || "Failed to parse BOQ file");
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || error.message || "Failed to parse BOQ file", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (column) => {
    setSelectedColumns((current) =>
      current.includes(column)
        ? current.filter((value) => value !== column)
        : [...current, column],
    );
  };

  const applyImport = () => {
    if (!items.length) {
      swal("Warning", "Parse a BOQ file first", "warning");
      return;
    }

    const normalized = items.map((item) => normalizePreviewItem(item, sacCode));
    onApply(normalized, {
      sourceFile: parsed?.source_file || file?.name || "",
      availableColumns,
      selectedColumns,
      rawItems: items,
    });
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Import BOQ From PDF</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fw-bold mb-2">Extraction Engine</div>
          <div className="d-flex flex-wrap gap-4 align-items-center mb-1">
            <Form.Check
              type="radio"
              id="engine-python"
              name="boq-extraction-engine"
              label={
                <span>
                  <strong>Default</strong>{" "}
                  <span className="text-muted small">— Fast rule-based table parser</span>
                </span>
              }
              value="python"
              checked={engine === "python"}
              onChange={() => setEngine("python")}
            />
            <Form.Check
              type="radio"
              id="engine-gemini"
              name="boq-extraction-engine"
              label={
                <span>
                  <strong>Gemini AI</strong>{" "}
                  <span className="text-muted small">— Multimodal AI for complex or unstructured formats</span>
                </span>
              }
              value="gemini"
              checked={engine === "gemini"}
              onChange={() => setEngine("gemini")}
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <Form.Control
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ maxWidth: 360 }}
          />
          <Button variant="primary" onClick={parseFile} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Parsing ({engine === "gemini" ? "Gemini AI" : "Default"})...
              </>
            ) : (
              `Parse BOQ with ${engine === "gemini" ? "Gemini AI" : "Default"}`
            )}
          </Button>
          {parsed?.items?.length ? (
            <Badge bg="success">{parsed.items.length} rows found</Badge>
          ) : null}
          {parsed?.engine_used ? (
            <Badge bg="info">
              Extracted by: {parsed.engine_used.includes("gemini") ? "Gemini AI" : "Default"}
            </Badge>
          ) : null}
        </div>

        {parsed?.metadata ? (
          <div className="mb-3 small text-muted">
            <div><strong>Source:</strong> {parsed.source_file || file?.name || "-"}</div>
            <div><strong>Pages:</strong> {parsed.metadata.page_count || parsed.pages?.length || 0}</div>
          </div>
        ) : null}

        {availableColumns.length > 0 && (
          <div className="border rounded p-3 mb-3 bg-light">
            <div className="fw-bold mb-2">Choose columns to show</div>
            <div className="d-flex flex-wrap gap-3">
              {availableColumns.map((column) => (
                <Form.Check
                  key={column}
                  type="checkbox"
                  id={`boq-col-${column}`}
                  label={labelMap[column] || column}
                  checked={selectedColumns.includes(column)}
                  onChange={() => toggleColumn(column)}
                />
              ))}
            </div>
          </div>
        )}

        {items.length > 0 ? (
          <div className="table-responsive border rounded" style={{ maxHeight: "60vh" }}>
            <Table bordered hover size="sm" className="mb-0 align-middle">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  {visibleColumns.map((column) => (
                    <th key={column}>{labelMap[column] || column}</th>
                  ))}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const normalized = normalizePreviewItem(item, sacCode);
                  const isValid =
                    normalized.itemName &&
                    normalized.description &&
                    normalized.unit &&
                    normalized.quantity > 0 &&
                    normalized.price >= 0;

                  return (
                    <tr key={`${item.page_number || 0}-${item.row_index || index}`}>
                      {visibleColumns.map((column) => {
                        const value = item?.[column];
                        const editable = EDITABLE_COLUMNS.has(column);

                        if (!editable) {
                          const display = Array.isArray(value)
                            ? value.join(", ")
                            : value === null || value === undefined || value === ""
                              ? "-"
                              : String(value);
                          return <td key={column}>{display}</td>;
                        }

                        if (column === "item_no" || column === "wl_no" || column === "hsn_sac" || column === "unit" || column === "item_name" || column === "description" || column === "payment_terms") {
                          return (
                            <td key={column}>
                              <Form.Control
                                size="sm"
                                value={value ?? ""}
                                onChange={(e) => updateItem(index, column, e.target.value)}
                              />
                            </td>
                          );
                        }

                        return (
                          <td key={column}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="any"
                              value={value ?? ""}
                              onChange={(e) => updateItem(index, column, e.target.value)}
                            />
                          </td>
                        );
                      })}
                      <td>
                        <Badge bg={isValid ? "success" : "warning"} text={isValid ? "light" : "dark"}>
                          {isValid ? "Ready" : "Needs review"}
                        </Badge>
                        {item.wrongly_formatted ? (
                          <Badge bg="danger" className="ms-2">Flagged</Badge>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-muted">Upload and parse a PDF to preview BOQ rows.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={applyImport} disabled={!items.length}>
          Apply to BOQ
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BoqImportModal;
